import { DOKUMENTE, gezogeneKapitel, type Dokumentart, type EigenesKapitel, type GezogenesKapitel } from './kapitel';
import { DYNAMISCHE_PLATZHALTER, KOPF_FELD_JE_PLATZHALTER, istDynamisch, type KopfFeld } from './bausteine/platzhalter';
import type {
    Abschnitt,
    Bauordnung,
    BauordnungBundle,
    KapitelZeile,
    Luecke,
    Regel,
    Rolle,
} from './types';
import type { ProjektMerkmale } from '../planungsphase/types';

/**
 * Die Regeln des Moduls 06 — rein, ohne DOM und ohne Supabase.
 *
 * Was hier steht, ist geprueft; was in einer `.tsx` steht, ist es nicht (im
 * Repo gibt es kein jsdom). Jede Entscheidung dieses Moduls gehoert deshalb
 * hierher: was fertig ist, was fehlt, was die Freigabe hindert.
 */

/**
 * Fassung des Regelwerks, mit der dieser Stand erfasst wurde. Eine Konstante,
 * ein Ort (Plan F11): sie lebt beim Katalog der Textbausteine und wird hier
 * nur weitergereicht — der Dienst und die Ablage lesen sie weiter von hier.
 */
export { KATALOG_VERSION } from './bausteine/katalog';

export const istGesperrt = (ordnung: Bauordnung | null): boolean =>
    ordnung?.status === 'freigegeben';

/** Umwandlung der Datenbankzeile in das, was `kapitel.ts` versteht. */
export const alsEigenesKapitel = (z: KapitelZeile): EigenesKapitel => ({
    id: z.kapitel_id,
    titel: z.titel,
    dokument: z.dokument,
    teil: z.teil,
    nach: z.nach ?? undefined,
    pflicht: z.pflicht,
    waehlbar: z.waehlbar,
    quelle: (z.quelle?.length ? z.quelle : ['erfasst']) as EigenesKapitel['quelle'],
    wenn: (z.wenn ?? []) as EigenesKapitel['wenn'],
    nichtWenn: (z.nicht_wenn ?? []) as EigenesKapitel['nichtWenn'],
});

/**
 * Die Kapitel eines Dokuments in Druckreihenfolge.
 *
 * Ab der Freigabe zaehlt der EINGEFRORENE Stand: ein unterschriebenes
 * Dokument darf nicht nachtraeglich um ein Kapitel wachsen, nur weil jemand
 * ein firmenweites Kapitel angelegt oder einen Termin verschoben hat.
 */
export const kapitelDesDokuments = (
    bundle: BauordnungBundle,
    dokument: Dokumentart,
    merkmale: ProjektMerkmale,
): GezogenesKapitel[] => {
    const o = bundle.ordnung;
    const eigene = bundle.eigeneKapitel.map(alsEigenesKapitel);
    const zusatz = o?.hse_zusatzkapitel ?? [];
    const alle = gezogeneKapitel(dokument, merkmale, dokument === 'hse' ? zusatz : [], eigene);

    const eingefroren = o?.status === 'freigegeben' ? o.eingefroren_kapitel?.[dokument] : undefined;
    if (!eingefroren?.length) return alle;

    const erlaubt = new Set(eingefroren);
    return alle.filter(g => erlaubt.has(g.definition.id));
};

// ═══════════════════════════════════════════════════════════════════════════
// BEFUNDE JE KAPITEL
// ═══════════════════════════════════════════════════════════════════════════

export interface AbschnittBefund {
    kapitel: string;
    titel: string;
    pflicht: boolean;
    /** Zeile in der Datenbank vorhanden? Fehlend ist NICHT dasselbe wie leer. */
    angesehen: boolean;
    /** Ausdruecklich geprueft und ausgeschlossen. */
    ausgeschlossen: boolean;
    /** Bestaetigte Regelungen — nur die zaehlen. */
    bestaetigt: number;
    /** Erfasst, aber noch nicht bestaetigt (darunter jeder KI-Vorschlag). */
    offen: number;
    vollstaendig: boolean;
}

/** Zaehlt eine Regel als erfasst? Nur mit Bestaetigung und mit Wortlaut. */
export const istBestaetigt = (r: Regel): boolean =>
    !!r.bestaetigt_am && r.wortlaut.trim().length > 0;

export const abschnittBefunde = (
    bundle: BauordnungBundle,
    dokument: Dokumentart,
    merkmale: ProjektMerkmale,
): AbschnittBefund[] => {
    const gezogen = kapitelDesDokuments(bundle, dokument, merkmale);
    const zeilen = new Map(bundle.abschnitte.map(a => [a.kapitel, a]));

    return gezogen.map(g => {
        const zeile = zeilen.get(g.definition.id);
        const regeln = bundle.regeln.filter(r => r.kapitel === g.definition.id);
        const bestaetigt = regeln.filter(istBestaetigt).length;
        const ausgeschlossen = !!zeile && !zeile.gezogen;

        return {
            kapitel: g.definition.id,
            titel: g.definition.titel,
            pflicht: g.pflicht,
            angesehen: !!zeile,
            ausgeschlossen,
            bestaetigt,
            offen: regeln.length - bestaetigt,
            // Ausgeschlossen zaehlt als erledigt: die Entscheidung IST die
            // Bearbeitung. Ein Pflichtkapitel ohne bestaetigte Regel ist
            // dagegen unvollstaendig, auch wenn es Vorschlaege traegt.
            vollstaendig: ausgeschlossen || zeile?.zustand === 'fertig' || bestaetigt > 0,
        };
    });
};

/** Wie weit ist ein Dokument? Zaehlt Kapitel, nicht Regeln. */
export const fortschritt = (
    bundle: BauordnungBundle,
    dokument: Dokumentart,
    merkmale: ProjektMerkmale,
): { fertig: number; gesamt: number; prozent: number } => {
    const befunde = abschnittBefunde(bundle, dokument, merkmale);
    const gesamt = befunde.length;
    const fertig = befunde.filter(b => b.vollstaendig).length;
    return { fertig, gesamt, prozent: gesamt ? Math.round((fertig / gesamt) * 100) : 0 };
};

// ═══════════════════════════════════════════════════════════════════════════
// FREIGABE
// ═══════════════════════════════════════════════════════════════════════════

export interface Hindernis {
    /** Wo es sitzt — fuer den Sprung in den richtigen Reiter. */
    bereich: 'grundlagen' | 'kapitel' | 'rollen' | 'offene_punkte';
    text: string;
}

/** So heissen die Kopfsatzfelder im Reiter „Grundlagen". */
const KOPF_FELD_LABEL: Partial<Record<KopfFeld, string>> = {
    stand: 'Stand',
    version_label: 'Fassung',
    geltung_von: 'Gültig von',
    geltung_bis: 'Gültig bis',
    geltung_ort: 'Geltungsbereich',
};

/** Erlassender und Unterzeichner haelt die Freigabe immer an — ein zweiter Satz waere doppelt. */
const SCHON_GEPRUEFT: ReadonlySet<KopfFeld> = new Set<KopfFeld>(['erlassender_firma', 'erlassender_name']);

/**
 * Die leeren Kopfsatzfelder, auf die eine GEDRUCKTE Regel verweist — in der
 * Reihenfolge der Platzhaltertabelle, jedes Feld einmal. Gedruckt heisst wie
 * in `baueDokument`: bestaetigt, in einem gezogenen Kapitel eines erzeugten
 * Dokuments, und das Kapitel nicht als „trifft nicht zu" ausgeschlossen.
 */
const leereKopfFelder = (
    bundle: BauordnungBundle,
    merkmale: ProjektMerkmale,
    dokumente: readonly Dokumentart[],
): KopfFeld[] => {
    const o = bundle.ordnung;
    if (!o) return [];
    const ausgeschlossen = new Set(bundle.abschnitte.filter(a => !a.gezogen).map(a => a.kapitel));
    const gedruckt = new Set<string>();
    for (const d of dokumente) {
        for (const g of kapitelDesDokuments(bundle, d, merkmale)) {
            if (!ausgeschlossen.has(g.definition.id)) gedruckt.add(g.definition.id);
        }
    }
    const texte = bundle.regeln.filter(r => istBestaetigt(r) && gedruckt.has(r.kapitel)).map(r => r.wortlaut);
    const leer: KopfFeld[] = [];
    for (const name of DYNAMISCHE_PLATZHALTER) {
        const feld = KOPF_FELD_JE_PLATZHALTER[name];
        if (!feld || leer.includes(feld) || String(o[feld] ?? '').trim()) continue;
        if (texte.some(t => t.includes(`{${name}}`))) leer.push(feld);
    }
    return leer;
};

/**
 * Was der Freigabe entgegensteht.
 *
 * Leere Liste heisst: die Freigabe ist moeglich. Die Liste ist der einzige
 * Ort, an dem das entschieden wird — der Knopf fragt sie, der Dienst fragt
 * sie noch einmal.
 */
export const freigabeHindernisse = (
    bundle: BauordnungBundle,
    merkmale: ProjektMerkmale,
): Hindernis[] => {
    const h: Hindernis[] = [];
    const o = bundle.ordnung;
    if (!o) return [{ bereich: 'grundlagen', text: 'Es gibt noch keine Baustellenordnung.' }];

    // ─── Der Erlassende ──────────────────────────────────────────────────
    // Ohne ihn ist das Dokument ein Aushang ohne Rechtswirkung: nur der
    // Erlassende hat das Hausrecht, und nur er unterschreibt.
    if (!o.erlassender_firma.trim()) {
        h.push({ bereich: 'grundlagen', text: 'Der Erlassende (Firma) fehlt — ohne ihn hat das Dokument keine Verbindlichkeit.' });
    }
    if (!o.erlassender_name.trim()) {
        h.push({ bereich: 'grundlagen', text: 'Der Unterzeichner des Erlassenden fehlt.' });
    }
    if (!o.katalog_version.trim()) {
        h.push({ bereich: 'grundlagen', text: 'Die Fassung des Regelwerks fehlt am Kopfsatz.' });
    }

    // ─── Weisungsbefugnis ────────────────────────────────────────────────
    // Eine unbegrenzt formulierte Weisungsbefugnis gegenueber fremden
    // Beschaeftigten ist das AUeG-Risiko.
    for (const r of bundle.rollen) {
        if (r.weisungsbefugnis === 'begrenzt_dguv1_6' && !r.befugnis_umfang.trim()) {
            h.push({
                bereich: 'rollen',
                text: `„${r.rolle || r.funktion || 'Rolle ohne Bezeichnung'}": die Begrenzung der Weisungsbefugnis ist nicht beschrieben.`,
            });
        }
    }

    // ─── Regeln mit Bestaetigung, aber ohne Wortlaut ─────────────────────
    // Eine bestaetigte Zeile ohne Satz druckt eine leere Regelung. Eine
    // UNBESTAETIGTE Zeile ohne Wortlaut hindert dagegen nicht — das ist ein
    // Entwurf, und Entwuerfe duerfen leer sein.
    for (const r of bundle.regeln) {
        if (r.bestaetigt_am && !r.wortlaut.trim()) {
            h.push({
                bereich: 'kapitel',
                text: `Kapitel „${r.kapitel}", Position ${r.sortierung + 1}: bestätigt, aber ohne Wortlaut${r.baustein_id ? ` (Baustein ${r.baustein_id})` : ''}.`,
            });
        }
    }

    // ─── Regeln mit Bestaetigung, aber mit Platzhalter ───────────────────
    // Eine bestaetigte Zeile druckt, was in ihr steht — auch „[Aushangort]".
    // Die Luecke dazu allein reicht nicht: sie laesst sich „erledigen", ohne
    // dass jemand den Text aendert (Plan F4). Deshalb haelt der Text selbst an.
    // Ausgenommen die dynamischen `{name}` des Kopfsatzes: sie loest der
    // Druck auf, ihr leeres Feld prueft der Block darunter.
    for (const r of bundle.regeln) {
        if (!istBestaetigt(r)) continue;
        const platzhalter = offenePlatzhalterIn(r.wortlaut);
        if (!platzhalter.length) continue;
        h.push({
            bereich: 'kapitel',
            text: `Kapitel „${r.kapitel}", Position ${r.sortierung + 1}: enthält noch `
                + `${platzhalter.length === 1 ? 'einen Platzhalter' : 'Platzhalter'} (${platzhalter.join(', ')}).`,
        });
    }

    const dokumente: Dokumentart[] = [];
    if (o.erzeugt_ordnung) dokumente.push('ordnung');
    if (o.erzeugt_hse) dokumente.push('hse');

    // ─── Kopfsatzfelder, auf die gedruckte Regeln verweisen ─────────────
    // `{geltung_ende}` & Co. stehen bis zum Druck im Wortlaut und werden dann
    // aus dem Kopfsatz gelesen. Ist das Feld leer, truege das freigegebene
    // Dokument „[Geltungsende]" — deshalb haelt das leere FELD an, ein Satz
    // je Feld, gleich wie viele Regeln darauf verweisen.
    for (const feld of leereKopfFelder(bundle, merkmale, dokumente)) {
        if (SCHON_GEPRUEFT.has(feld)) continue;
        h.push({
            bereich: 'grundlagen',
            text: `${KOPF_FELD_LABEL[feld] ?? feld} fehlt am Kopfsatz — Regelungen verweisen darauf.`,
        });
    }

    // ─── Offene PFLICHT-Punkte ───────────────────────────────────────────
    const offenePflicht = bundle.luecken.filter(l => l.pflicht && !l.erledigt_am);
    for (const l of offenePflicht) {
        h.push({ bereich: 'offene_punkte', text: `Offener Pflichtpunkt Nr. ${l.nr}: ${l.punkt}` });
    }

    // ─── Leere Pflichtkapitel ────────────────────────────────────────────
    if (!dokumente.length) {
        h.push({ bereich: 'grundlagen', text: 'Es ist kein Dokument zur Erzeugung ausgewählt.' });
    }
    for (const d of dokumente) {
        for (const b of abschnittBefunde(bundle, d, merkmale)) {
            if (b.pflicht && !b.vollstaendig) {
                h.push({
                    bereich: 'kapitel',
                    text: `${DOKUMENTE[d].titel}: Pflichtkapitel „${b.titel}" ist noch leer.`,
                });
            }
        }
    }
    return h;
};

// ═══════════════════════════════════════════════════════════════════════════
// OFFENE PUNKTE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die naechste freie Nummer.
 *
 * Nummern werden NIE neu vergeben: eine geloeschte Luecke hinterlaesst eine
 * Zaehluecke, und das ist richtig so — sonst wird jeder aeltere Verweis auf
 * „Nr. 4" falsch.
 */
export const naechsteLueckenNummer = (luecken: Luecke[]): number =>
    luecken.reduce((max, l) => Math.max(max, l.nr), 0) + 1;

/** Erledigte bleiben in der Liste, sie verschwinden nicht. */
export const sortiereLuecken = (luecken: Luecke[]): Luecke[] =>
    [...luecken].sort((a, b) => {
        const aOffen = a.erledigt_am ? 1 : 0;
        const bOffen = b.erledigt_am ? 1 : 0;
        if (aOffen !== bOffen) return aOffen - bOffen;
        return a.nr - b.nr;
    });

/**
 * Unersetzte Platzhalter im Wortlaut — jeder erzeugt einen offenen Punkt.
 *
 * Zwei Formen (Plan F3): `[…]` (so schreibt die Automatik Unersetztes, so
 * stehen Platzhalter in Mustertexten) und `{name}` (die Form des Katalogs —
 * ein Baustein, der ungefuellt durchrutscht, darf nicht still gedruckt
 * werden). Reihenfolge des Auftretens, gleiche Treffer nur einmal.
 */
export const platzhalterIn = (text: string): string[] => {
    // Auch ein einzeichiger Platzhalter zaehlt: `[X]` steht in Mustern
    // genauso haeufig wie `[Ort eintragen]`.
    const treffer = (text || '').match(/\[[^\]\n]{1,60}\]|\{[a-z0-9_]+\}/g);
    return treffer ? Array.from(new Set(treffer)) : [];
};

/**
 * Wie `platzhalterIn`, aber ohne die dynamischen `{name}` des Kopfsatzes
 * (`{revision_nr}`, `{geltung_ende}` …) — die bleiben absichtlich stehen und
 * werden beim Druck aufgeloest. Was hier uebrig bleibt, braucht eine Antwort.
 *
 * Nur die Katalogform zaehlt als dynamisch: ein `[Stand]` im Text hat schon
 * jemand (oder ein frueherer Lauf) als Luecke geschrieben — es bleibt offen,
 * bis eine Antwort es ersetzt.
 */
export const offenePlatzhalterIn = (text: string): string[] =>
    platzhalterIn(text).filter(t => !(t.startsWith('{') && istDynamisch(t.slice(1, -1))));

// ═══════════════════════════════════════════════════════════════════════════
// SORTIERUNG UND VORLAGEN
// ═══════════════════════════════════════════════════════════════════════════

export const regelnDesKapitels = (regeln: Regel[], kapitel: string): Regel[] =>
    regeln.filter(r => r.kapitel === kapitel).sort((a, b) => a.sortierung - b.sortierung);

export const sortiereRollen = (rollen: Rolle[]): Rolle[] =>
    [...rollen].sort((a, b) => a.sortierung - b.sortierung);

export const abschnittVon = (abschnitte: Abschnitt[], kapitel: string): Abschnitt | undefined =>
    abschnitte.find(a => a.kapitel === kapitel);

/** Ein Eintrag der Historie, immer mit Datum und Handelndem. */
export const ergaenzeHistorie = (
    bisher: Bauordnung['historie'],
    wer: string,
    was: string,
    amIso: string,
): Bauordnung['historie'] => [...(bisher || []), { am: amIso, wer: wer || 'unbekannt', was }];
